/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMutation } from '@apollo/client';
import { Button, Stack, Text, TextInput, rem, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import i18n from '@fai/friendli-suite/features/i18n';
import { DedicatedModelArchiveDocument } from '@fai/friendli-suite/generated/client/graphql';
import { errorProps } from '@fai/ui/components/notification/props';
import { FooterlessModalFooter } from '@fai/ui/mantine-modal/helpers';
import { type Nullish } from '@fai/util/types';

const archiveModelModalId = 'dedicated-endpoints-archive-model-modal';
const closeArchiveModelModal = () => modals.close(archiveModelModalId);

export const openArchiveModelModal = (
  props: React.ComponentProps<typeof ArchiveModelModalBody>
) => {
  modals.openContextModal({
    modal: 'footerless',
    modalId: archiveModelModalId,
    size: rem(560),
    title: i18n.t('dedicated_endpoints.project.archive_model'),
    innerProps: { modalBody: <ArchiveModelModalBody {...props} /> },
  });
};

const ArchiveModelModalBody = ({
  modelName,
  modelId,
  redirectPath,
}: {
  modelName: Nullish<string>;
  modelId: string;
  redirectPath: string;
}) => {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [value, setValue] = React.useState('');

  const [archiveModel, { loading }] = useMutation(DedicatedModelArchiveDocument, {
    variables: { input: { modelId } },
    onCompleted: (d) => {
      switch (d.dedicatedModelArchive.__typename) {
        case 'DedicatedModelArchiveSuccess': {
          closeArchiveModelModal();
          navigate(redirectPath);

          break;
        }

        case 'UserPermissionError': {
          notifications.show({
            ...errorProps,
            message: d.dedicatedModelArchive.message,
          });

          break;
        }
      }
    },
  });

  return (
    <Stack>
      <Text c={theme.pf.color.fg.neutral.strong}>
        {t('dedicated_endpoints.project.when_then_model_is_archived')}
      </Text>
      <Stack gap={theme.pf.layout['2xs']}>
        <Text c={theme.pf.color.fg.neutral.strong}>
          <Trans
            i18nKey="dedicated_endpoints.project.please_type_model_name"
            components={[<b />]}
            values={{ modelName }}
          />
        </Text>
        <TextInput
          value={value}
          onChange={(event) => setValue(event.currentTarget.value)}
          placeholder={`${t('dedicated_endpoints.project.enter_model_name')}`}
        />
      </Stack>

      <FooterlessModalFooter>
        <Button variant="transparent" onClick={closeArchiveModelModal}>
          {t('shared.cancel')}
        </Button>
        <Button
          theme="cloud"
          disabled={modelName !== value || loading}
          onClick={() => {
            archiveModel();
          }}
        >
          {t('dedicated_endpoints.project.archive')}
        </Button>
      </FooterlessModalFooter>
    </Stack>
  );
};
